<script setup lang="ts">
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import CoachingCategoryLabel from './CoachingCategoryLabel.vue';
    import CoachingFormController from './form/CoachingFormController.vue';
    import IconButton from '../IconButton.vue';
    import { computed, onMounted, ref, watch } from 'vue';
    import ToggleSwitch from '../ToggleSwitch.vue';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import {
        useGetCoachingForm,
        useGetCoachingFormProtocolsUsage,
        useMutateDeleteCoachingForm,
        useMutateUpdateCoachingFormInactiveState,
    } from '@/js/services/api/coachingForm/coachingFormQueries';
    import { useRoute, useRouter } from 'vue-router';
    import LoadingDots from '../LoadingDots.vue';
    import dayjs from 'dayjs';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, QualityAssuranceModulePermissionFlag } from '@/js/consts/permissions';
    import { CopyIcon, TrashIcon } from '@lucide/vue';

    const { hasPermission } = usePermissions();

    const canCreateForm = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.CreateForm
    );

    const confirm = useConfirmationDialog();

    const route = useRoute();

    const router = useRouter();

    const formId = computed(() => route.params.id as string);

    const { data: form, isLoading, refetch } = useGetCoachingForm(formId);

    const { mutateAsync: deleteForm } = useMutateDeleteCoachingForm(formId.value);

    const { mutateAsync: updateInactiveState } = useMutateUpdateCoachingFormInactiveState(formId);

    const { data: protocolsUsageData } = useGetCoachingFormProtocolsUsage(formId.value);

    onMounted(async () => {
        await router.isReady();
        await refetch();
    });

    const deleteMessageDependingOnUsage = computed(() => {
        if (!protocolsUsageData.value || protocolsUsageData.value.numberOfProtocols === 0) {
            return __('coaching-form-confirm-delete-description');
        }

        return __('coaching-form-confirm-delete-used-description', {
            lastUsedAt: dayjs(protocolsUsageData.value.lastUsedAt).format('YYYY-MM-DD'),
            numberOfProtocols: String(protocolsUsageData.value.numberOfProtocols),
        });
    });

    const confirmActions = () => ({
        delete: {
            title: __('coaching-form-confirm-delete-title'),
            message: deleteMessageDependingOnUsage.value,
            confirmText: __('coaching-form-confirm-delete-button'),
            callback: async () => {
                const force = (protocolsUsageData.value?.numberOfProtocols ?? 0) > 0;
                await deleteForm(force);
                router.push('/settings/manage-forms');
            },
        },
        disable: {
            title: __('coaching-form-confirm-disable-title'),
            message: __('coaching-form-confirm-disable-description'),
            confirmText: __('coaching-form-confirm-disable-button'),
            callback: () => {},
        },
        enable: {
            title: __('coaching-form-confirm-enable-title'),
            message: __('coaching-form-confirm-enable-description'),
            confirmText: __('coaching-form-confirm-enable-button'),
            callback: () => {},
        },
    });

    const handleMenuClick = async (action: 'copy' | 'delete') => {
        const actions = confirmActions();

        if (action === 'copy') {
            router.push(`/settings/manage-forms/${formId.value}/copy`);
        } else if (action === 'delete') {
            await confirm.openDialog(actions[action]);
        }

        return;
    };

    const isActive = ref<boolean>(false);
    const handleToggle = async (value: boolean | undefined) => {
        if (value === undefined) return;

        const actions = confirmActions();
        const actionKey = value ? 'enable' : 'disable';

        const confirmed = await confirm.openDialog(actions[actionKey]);

        if (!confirmed) {
            isActive.value = !value;
            return;
        }

        try {
            await updateInactiveState(value);
        } catch {
            isActive.value = !value;
        }
    };

    watch(
        () => form.value?.isActive,
        (val) => {
            if (val !== undefined) {
                isActive.value = val;
            }
        },
        { immediate: true }
    );
</script>
<template>
    <div v-if="!isLoading && form" class="pb-5">
        <div class="flex items-center mb-6 justify-between">
            <div class="flex items-center gap-4">
                <h2 class="heading-lg">
                    {{ form.title.trim() || __('coaching-form-untitled') }}
                </h2>
                <CoachingCategoryLabel :ai="form.useAi">
                    <p>
                        {{ __untypedExpression(form.useAi ? 'ai' : 'manual') }}
                    </p>
                </CoachingCategoryLabel>
                <h3 v-if="!form.isActive" class="heading-sm uppercase">
                    {{ __('inactive') }}
                </h3>
            </div>

            <div v-if="canCreateForm" class="flex items-center">
                <IconButton
                    :tooltip="__('copy-form')"
                    :aria-label="__('copy')"
                    size="size-6"
                    area-size="size-8"
                    @click="handleMenuClick('copy')"
                >
                    <CopyIcon class="size-5" />
                </IconButton>
                <IconButton
                    :tooltip="__('delete-form')"
                    :aria-label="__('delete')"
                    size="size-6"
                    area-size="size-8"
                    @click="handleMenuClick('delete')"
                >
                    <TrashIcon class="size-5" />
                </IconButton>
                <div class="flex items-center gap-2 ml-2">
                    <ToggleSwitch v-model="isActive" @update:model-value="handleToggle" />
                    <span class="text-body mr-2 w-8">
                        {{ isActive ? __('active') : __('inactive') }}
                    </span>
                </div>
            </div>
        </div>
        <CoachingFormController :form="form" :preview="true" />
    </div>
    <div v-else class="h-32 flex items-center justify-center">
        <LoadingDots />
    </div>
</template>
